<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import AppToast from "./AppToast.vue";
import Cookies from 'js-cookie'
import { HTTP } from '@/Api/http-common';
import { useRouter } from 'vue-router';
import mtnLogo from '../assets/logo-mtn.png'
// import minLogo from '../assets/logo.png'

const reasonCodeMessages = {
  0: 'Success',
  2: 'خطأ في تنسيق البيانات أو التحقق من الصحة',
  103: 'خطأ في الفاتورة (رصيد غير كافٍ)',
  104: 'حساب المستخدم في حالة غير نشطة',
  105: 'المستخدم غير معروف',
  106: 'تم عكس الشحنة بالفعل',
  108: 'الرسوم تتجاوز حد المعاملة',
  115: 'تم الوصول إلى الحد الأقصى للسعر العالمي',
  116: 'تم الوصول إلى حد المعدل للمستخدم',
  117: 'المستخدم في حالة عدم أهلية دائمة',
  118: 'المستخدم غير مؤهل للحصول على فاتورة الناقل',
  119: 'فشل متعلق بالمستخدم',
  200: 'رمز PIN غير صالح',
  201: 'انتهت صلاحية كلمة المرور لمرة واحدة',
  202: 'تم الوصول إلى الحد الأقصى لمحاولات التحقق من OTP',
  123: 'في تَقَدم'
};

// eslint-disable-next-line no-undef
const emit = defineEmits(['verification-success', 'back'])

const router = useRouter()
const verificationCode = ref('')
const loading = ref(false)
const error = ref('')
const timeLeft = ref(300) // 5 minutes in seconds
const timerInterval = ref(null)

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const startTimer = () => {
  timeLeft.value = 300 // Reset to 5 minutes
  clearInterval(timerInterval.value)
  
  timerInterval.value = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value--
    } else {
      clearInterval(timerInterval.value)
      error.value = reasonCodeMessages[201] // OTP expired
    }
  }, 1000)
}

onMounted(() => {
  startTimer()
})

onBeforeUnmount(() => {
  clearInterval(timerInterval.value)
})

const showToast = ref(false);
const toastMessage = ref('');
const toastType = ref('success');

// const seatNumber = ref(Cookies.get('seat_number') || '')
const phoneNumber = ref(Cookies.get('phone_number') || '')

const handleSubmit = async () => {
  if (timeLeft.value === 0) {
    error.value = reasonCodeMessages[201] // OTP expired
    return
  }
  
  if (!verificationCode.value) {
    error.value = 'الرجاء إدخال رمز التحقق'
    return
  }

  loading.value = true
  error.value = ''

  try {
    const payload = {
      ValidateOTP: {
        requestId: Cookies.get('requestID') || '',
        otp: verificationCode.value,
      }
    }
    const response = await HTTP.post('VerifyOTP.php', payload);

    if (response && response.data) {
      const data = response.data;
      if (data.error === false && data.status === 'OK' && data.reasonCode === 0 && data.result) {
        // Map the result data based on new API structure
        const mappedResult = {
          name: '', // Not provided in new structure
          seatNumber: data.seat_number || data.result.form_no || '',
          totalScore: '', // Not provided in new structure
          average: '', // Not provided in new structure
          result: data.result.result || '',
          subjects: null // Not provided in new structure
        };

        // Store result in sessionStorage
        sessionStorage.setItem('student_result', JSON.stringify(mappedResult));

        // Show success toast
        toastMessage.value = data.message_ar || 'تم التحقق بنجاح';
        toastType.value = 'success';
        showToast.value = true;

        // Wait for toast then navigate
        setTimeout(() => {
          loading.value = false;
          // Since there are no subjects in the new structure, always navigate to simple result
          router.push({ 
            name: 'student-result',
            params: { result: mappedResult }
          });
        }, 1000);
      } else if (data.error === false && data.status === 'OK' && data.reasonCode === 0) {
        // Handle case when verification is successful but no result
        error.value = 'لا توجد نتيجة متاحة لهذا الرقم';
        loading.value = false;
      } else if (data.status === 'EXPIRED' && data.error === true) {
        // Handle OTP expired case
        error.value = data.message_ar || 'انتهت صلاحية رمز التفعيل';
        timeLeft.value = 0; // Stop the timer
        clearInterval(timerInterval.value);
        loading.value = false;
      } else {
        // Handle ValidateOTP error codes
        let reasonMsg = reasonCodeMessages[data.reasonCode] || data?.message_ar || data?.message || 'فشل التحقق من الرمز';
        if (data.reasonCode === 200) {
          reasonMsg = reasonCodeMessages[200]; // Invalid Pin-Code
        } else if (data.reasonCode === 201) {
          reasonMsg = reasonCodeMessages[201]; // OTP expired
        } else if (data.reasonCode === 202) {
          reasonMsg = reasonCodeMessages[202]; // Max attempts
        } else if (data.reasonCode === 123) {
          reasonMsg = reasonCodeMessages[123]; // In Progress
        }
        error.value = reasonMsg;
        loading.value = false;
      }
    } else {
      loading.value = false;
      error.value = 'فشل التحقق من الرمز';
    }
  } catch (e) {
    loading.value = false;
    error.value = 'حدث خطأ أثناء التحقق';
    if (process.env.NODE_ENV === 'development') {
      console.error('OTP verification error:', e);
    }
  }
}
</script>

<template>
  <div class="page-container">
    <div class="container my-4">
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-5">
          <div class="verification-card animate__animated animate__fadeIn">
            <div class="card-header">
              <div class="logo-row">
                <!-- <img :src="minLogo" alt="وزارة التعليم" class="mtn-logo" /> -->
                <img :src="mtnLogo" alt="MTN Logo" class="mtn-logo" />
              </div>
              <div class="wave-pattern"></div>
            </div>
            <div class="card-body p-4">
              <div class="text-center">
                <h2 class="verification-title font-arabic">التحقق من رقم الهاتف</h2>
                <div class="phone-container">
                  <div class="phone-label font-arabic">تم إرسال رمز التحقق إلى الرقم</div>
                  <div class="phone-number">{{ phoneNumber }}</div>
                </div>
                <div class="timer-section" :class="{ 'expiring': timeLeft <= 60 }">
                  <div class="timer-circle">
                    <div class="timer-inner">
                      <span class="time">{{ formatTime(timeLeft) }}</span>
                      <span class="timer-label font-arabic">الوقت المتبقي</span>
                    </div>
                  </div>
                </div>

                <form @submit.prevent="handleSubmit" class="verification-form">
                  <div class="otp-input-container">
                    <input v-model="verificationCode" type="text" placeholder="أدخل رمز التحقق"
                      class="otp-input font-arabic" maxlength="4" autofocus
                      :disabled="timeLeft === 0 || loading" />
                    <div class="input-focus-border"></div>
                  </div>
                  
                  <p v-if="error" class="error-message font-arabic">{{ error }}</p>

                  <div class="buttons-container">
                    <button type="submit" :disabled="loading || timeLeft === 0" class="verify-button font-arabic">
                      <span v-if="loading" class="loading-spinner"></span>
                      <span v-if="loading">جاري التحقق...</span>
                      <span v-else>تحقق من الرمز</span>
                    </button>
                    
                    <button v-if="timeLeft === 0" type="button" @click="emit('back')" class="resend-button font-arabic">
                      طلب رمز جديد
                    </button>
                    
                    <button type="button" @click="emit('back')" class="back-button font-arabic">
                      عودة للصفحة السابقة
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AppToast v-if="showToast" :message="toastMessage" :type="toastType" @close="showToast = false" />
  </div>
</template>

<style scoped>
.page-container {
  min-height: 100vh;
  /* background: linear-gradient(135deg, #fffbe6 0%, #fff9c4 40%, #fff 100%); */
  padding: 2rem 0;
}

.verification-card {
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 15px 35px rgba(255, 196, 3, 0.1);
  overflow: hidden;
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.verification-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(255, 196, 3, 0.15);
}

.card-header {
  background: linear-gradient(135deg, #ffc403 0%, #ffed4a 100%);
  padding: 2rem 1rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.logo-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1rem;
}

.wave-pattern {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23ffffff' d='M0,96L48,112C96,128,192,160,288,154.7C384,149,480,107,576,90.7C672,75,768,85,864,96C960,107,1056,117,1152,122.7C1248,128,1344,128,1392,128L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E");
  background-size: cover;
}

.mtn-logo {
  height: 5rem;
  width: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  margin: 0;
  display: block;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

.verification-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #222;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #000 0%, #333 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.phone-container {
  background: linear-gradient(145deg, #fff8e1 0%, #fff 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  border: 2px solid rgba(255, 196, 3, 0.2);
  box-shadow: 0 8px 20px rgba(255, 196, 3, 0.08);
}

.phone-number {
  font-size: 1.4rem;
  font-weight: 700;
  color: #222;
  display: block;
  direction: ltr;
}

.phone-label {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.phone-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #000;
  letter-spacing: 0.5px;
  direction: ltr;
  background: linear-gradient(135deg, #000 0%, #333 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.timer-section {
  margin: 2rem 0;
  text-align: center;
}

.timer-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffd600 0%, #ffed4a 100%);
  padding: 4px;
  margin: 0 auto;
  position: relative;
  box-shadow: 0 10px 25px rgba(255, 214, 0, 0.2);
  animation: rotate 10s linear infinite;
}

.timer-inner {
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 4px 15px rgba(0, 0, 0, 0.05);
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.timer-circle .time {
  font-size: 1.3rem;
  font-weight: 700;
  color: #000;
  font-family: monospace;
}

.timer-text {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.expiring .timer-circle {
  animation: pulse 1s infinite;
  background: linear-gradient(135deg, #ff4d4d 0%, #ff8080 100%);
}

.expiring .time {
  color: #fff;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 4px 15px rgba(255, 77, 77, 0.2);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 4px 20px rgba(255, 77, 77, 0.4);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 15px rgba(255, 77, 77, 0.2);
  }
}

.verification-form {
  padding: 1rem;
}

.otp-input-container {
  position: relative;
  margin: 2rem 0;
}

.otp-input {
  width: 100%;
  padding: 1rem;
  font-size: 1.2rem;
  text-align: center;
  letter-spacing: 0rem;
  background: linear-gradient(145deg, #fff8e1 0%, #fff 100%);
  border: 2px solid rgba(255, 196, 3, 0.3);
  border-radius: 16px;
  outline: none;
  transition: all 0.3s ease;
}

.otp-input:focus {
  border-color: #ffc403;
  box-shadow: 0 8px 25px rgba(255, 196, 3, 0.15);
  transform: translateY(-2px);
}

.input-focus-border {
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 0;
  height: 2px;
  background: linear-gradient(135deg, #ffc403 0%, #ffed4a 100%);
  transition: width 0.3s ease;
}

.otp-input:focus ~ .input-focus-border {
  width: 100%;
}

.buttons-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

.verify-button {
  background: linear-gradient(135deg, #ffc403 0%, #ffed4a 100%);
  color: #000;
  font-weight: 700;
  font-size: 1.1rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 16px;
  width: 100%;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.verify-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 196, 3, 0.2);
}

.verify-button:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.5));
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.verify-button:hover:before {
  transform: translateX(100%);
}

.verify-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.back-button {
  background: transparent;
  border: 2px solid #ffc403;
  color: #000;
  font-weight: 600;
  font-size: 1rem;
  padding: 1rem;
  border-radius: 16px;
  width: 100%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-button:hover {
  background: rgba(255, 196, 3, 0.1);
  transform: translateY(-2px);
}

.resend-button {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  padding: 1rem;
  border: none;
  border-radius: 16px;
  width: 100%;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.2);
}

.resend-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 107, 107, 0.3);
}

.error-message {
  color: #ff4d4d;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
  animation: shake 0.5s ease;
}

.font-arabic {
  font-family: 'MTNBrighterSans', sans-serif;
}

.animate__animated.animate__fadeIn {
  animation-duration: 0.7s;
}

.loading-spinner {
  display: inline-block;
  width: 1.2rem;
  height: 1.2rem;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #000;
  border-radius: 50%;
  margin-right: 0.5rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.time {
  font-size: 1.8rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 0.2rem;
}

.timer-label {
  font-size: 0.8rem;
  color: #666;
}

.expiring .timer-inner {
  background: linear-gradient(135deg, #ff4d4d 0%, #ff8080 100%);
}

.expiring .time,
.expiring .timer-label {
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
  .page-container {
    padding: 1rem;
  }

  .verification-card {
    border-radius: 20px;
  }

  .card-header {
    padding: 1.5rem 1rem;
  }

  .mtn-logo {
    height: 4rem;
  }

  .verification-title {
    font-size: 1.5rem;
  }

  .phone-container {
    padding: 1rem;
    margin: 1rem 0;
  }

  .phone-number {
    font-size: 1.2rem;
  }

  .timer-circle {
    width: 100px;
    height: 100px;
  }

  .time {
    font-size: 1.5rem;
  }

  .otp-input {
    font-size: 1rem;
    letter-spacing: 0.1rem;
    padding: 0.8rem;
  }

  .verify-button,
  .back-button {
    padding: 0.8rem;
    font-size: 1rem;
  }
}

@media (max-width: 360px) {
  .verification-card {
    border-radius: 16px;
  }

  .timer-circle {
    width: 80px;
    height: 80px;
  }

  .time {
    font-size: 1.2rem;
  }

  .otp-input {
    font-size: 1.3rem;
    letter-spacing: 0.6rem;
  }
}
</style>